import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import CartDrawer from '../components/store/CartDrawer';
import './ProdutoDetalhe.css';
import API_BASE_URL from '../config/api';

const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http')) return imageUrl;
  return `${API_BASE_URL}${imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl}`;
};

export default function ProdutoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, totalItems, setIsDrawerOpen } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addedFeedback, setAddedFeedback] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customNumber, setCustomNumber] = useState('');

  // selectedChoices: { [variantType]: value }
  const [selectedChoices, setSelectedChoices] = useState({});
  const [kitPieceSelections, setKitPieceSelections] = useState({});
  const [selectedAthleteId, setSelectedAthleteId] = useState('');
  const [actualAthleteProfileId, setActualAthleteProfileId] = useState(null);
  const [athleteTeams, setAthleteTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedTeamName, setSelectedTeamName] = useState('');
  const [takenJerseyNumbers, setTakenJerseyNumbers] = useState([]);

  const [linkedAthletes, setLinkedAthletes] = useState([]);
  const [eligibleAthletes, setEligibleAthletes] = useState([]);
  const [athletesTeamsCache, setAthletesTeamsCache] = useState({});

  const isMember = !!localStorage.getItem('token');
  const isKit = product?.categoryName?.toLowerCase() === 'kits' || product?.categoryName?.toLowerCase() === 'kit';

  useEffect(() => {
    const storedLinked = localStorage.getItem('linkedUsers');
    let athletes = [];
    if (storedLinked) {
      try {
        const parsed = JSON.parse(storedLinked);
        athletes = parsed.filter(u => u.dashboardType === 'atleta' || (u.roles && u.roles.some(r => r.toLowerCase() === 'atleta')));
      } catch (e) { }
    }

    const currentUserRoles = JSON.parse(localStorage.getItem('roles') || '[]');
    if (currentUserRoles.some(r => r.toLowerCase() === 'atleta')) {
      const currentId = parseInt(localStorage.getItem('userId'), 10);
      if (!athletes.some(a => a.id === currentId)) {
        athletes.push({
          id: currentId,
          firstName: (localStorage.getItem('userName') || '').split(' ')[0],
          lastName: (localStorage.getItem('userName') || '').split(' ').slice(1).join(' '),
        });
      }
    }
    setLinkedAthletes(athletes);
  }, []);

  useEffect(() => {
    if (!isKit || linkedAthletes.length === 0 || !isMember) {
      setEligibleAthletes([]);
      return;
    }

    const fetchEligibility = async () => {
      const token = localStorage.getItem('token');
      const cache = {};
      const eligible = [];

      await Promise.all(linkedAthletes.map(async (a) => {
        try {
          const res = await fetch(`${API_BASE_URL}/api/users/${a.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            let teams = data.athleteProfile?.teams || [];
            if (product?.sportName) {
              teams = teams.filter(t => t.sportName === product.sportName);
            }
            cache[a.id] = {
              teams,
              actualAthleteProfileId: data.athleteProfile?.id || null
            };
            if (!product?.sportName || teams.length > 0) {
              eligible.push(a);
            }
          }
        } catch (e) { }
      }));

      setAthletesTeamsCache(cache);
      setEligibleAthletes(eligible);
    };

    fetchEligibility();
  }, [linkedAthletes, isKit, isMember, product?.sportName]);

  useEffect(() => {
    if (selectedAthleteId && athletesTeamsCache[selectedAthleteId]) {
      setAthleteTeams(athletesTeamsCache[selectedAthleteId].teams);
      setActualAthleteProfileId(athletesTeamsCache[selectedAthleteId].actualAthleteProfileId);
    } else {
      setAthleteTeams([]);
      setActualAthleteProfileId(null);
    }
  }, [selectedAthleteId, athletesTeamsCache]);

  useEffect(() => {
    if (selectedTeamId) {
      fetch(`${API_BASE_URL}/api/teams/${selectedTeamId}/jersey-numbers`)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data && data.usedNumbers) {
            setTakenJerseyNumbers(data.usedNumbers);
          } else {
            setTakenJerseyNumbers([]);
          }
        })
        .catch(err => {
          console.error(err);
          setTakenJerseyNumbers([]);
        });
    } else {
      setTakenJerseyNumbers([]);
    }
  }, [selectedTeamId]);



  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/products/${id}`)
      .then(r => {
        if (!r.ok) throw new Error('Produto não encontrado');
        return r.json();
      })
      .then(data => {
        setProduct(data);
        // Auto-select first value per group (stock irrelevant)
        const initialChoices = {};
        const groups = buildVariantGroups(data.variants || []);
        groups.forEach(group => {
          if (group.values.length > 0) initialChoices[group.type] = group.values[0];
        });
        setSelectedChoices(initialChoices);

        // Init Kit Pieces Selections
        if (data.categoryName?.toLowerCase() === 'kits' || data.categoryName?.toLowerCase() === 'kit') {
          if (data.kitItems && data.kitItems.length > 0) {
            const initialKitSelections = {};
            data.kitItems.forEach(ki => {
              for (let i = 0; i < ki.quantity; i++) {
                initialKitSelections[`${ki.kitPieceId}_${i}`] = {
                  kitPieceId: ki.kitPieceId,
                  pieceName: ki.pieceName,
                  unitIndex: i,
                  selectedSize: '',
                  customName: '',
                  customNumber: ''
                };
              }
            });
            setKitPieceSelections(initialKitSelections);
          }
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  /** Group active variants by variantType */
  const buildVariantGroups = (variants) => {
    // Legacy variants without variantType — treat size as "Tamanho", color as "Cor"
    const groups = {};
    variants.filter(v => v.isActive).forEach(v => {
      // Determine type label
      const type = v.variantType || (v.size ? 'Tamanho' : v.color ? 'Cor' : null);
      if (!type) return;
      // Determine value
      const value = v.size || v.color;
      if (!value) return;
      if (!groups[type]) groups[type] = { type, values: [] };
      if (!groups[type].values.includes(value)) {
        groups[type].values.push(value);
      }
    });
    return Object.values(groups);
  };

  const variantGroups = useMemo(() => buildVariantGroups(product?.variants || []), [product]);

  const selectedVariant = useMemo(() => {
    if (!product) return null;

    // No variant groups — return first active variant
    if (variantGroups.length === 0) {
      return product.variants.find(v => v.isActive) || null;
    }

    // All groups must have a selection
    if (Object.keys(selectedChoices).length < variantGroups.length) return null;

    // Each variant record represents ONE type (e.g. "Tamanho: M" or "Cor: Azul").
    // When multiple types exist, find the variant matching the FIRST group's selection —
    // the other choices are stored as metadata in variantDescription.
    for (const group of variantGroups) {
      const chosen = selectedChoices[group.type];
      if (!chosen) continue;
      const match = product.variants.find(v => {
        if (!v.isActive) return false;
        const type = v.variantType || (v.size ? 'Tamanho' : v.color ? 'Cor' : null);
        const value = v.size || v.color;
        return type === group.type && value === chosen;
      });
      if (match) return match;
    }
    return null;
  }, [product, selectedChoices, variantGroups]);

  // Human-readable label combining ALL selected choices (e.g. "Tamanho: M · Cor: Azul")
  const combinedDescription = Object.entries(selectedChoices)
    .map(([type, value]) => `${type}: ${value}`)
    .join(' · ') || null;

  /** All values are always available — stock is always assumed infinite */
  const isValueAvailable = () => true;

  const selectedTeamObj = useMemo(() => {
    if (!selectedTeamId) return null;
    return athleteTeams.find(t => t.id.toString() === selectedTeamId.toString()) || null;
  }, [selectedTeamId, athleteTeams]);

  const minNumber = isKit && selectedTeamObj?.jerseyNumberMin != null ? selectedTeamObj.jerseyNumberMin : 0;
  const maxNumber = isKit && selectedTeamObj?.jerseyNumberMax != null ? selectedTeamObj.jerseyNumberMax : 99;

  const isNumberValidIfRequired = !isKit || (
    customNumber !== '' && Number(customNumber) >= minNumber && Number(customNumber) <= maxNumber
  );

  const kitRequiresName = useMemo(() => {
    if (!isKit || !product?.kitItems) return false;
    return product.kitItems.some(ki => ki.customizationType === 'NameAndNumber');
  }, [isKit, product]);

  const kitRequiresNumber = useMemo(() => {
    if (!isKit || !product?.kitItems) return false;
    return product.kitItems.some(ki => ki.customizationType === 'NameAndNumber' || ki.customizationType === 'NumberOnly');
  }, [isKit, product]);

  const isKitPiecesValid = useMemo(() => {
    if (!isKit) return true;
    if (!product?.kitItems || product.kitItems.length === 0) return true;

    const allSizesSelected = Object.values(kitPieceSelections).every(sel => !!sel.selectedSize);
    if (!allSizesSelected) return false;

    if (kitRequiresName) {
      if (!customName || customName.trim() === '') return false;
    }
    
    if (kitRequiresNumber) {
      if (customNumber === '' || Number(customNumber) < minNumber || Number(customNumber) > maxNumber) return false;
    }

    return true;
  }, [isKit, kitPieceSelections, product?.kitItems, kitRequiresName, kitRequiresNumber, customName, customNumber, minNumber, maxNumber]);

  const allGroupsSelected = variantGroups.length === 0 || variantGroups.every(g => selectedChoices[g.type]);
  const isAthleteSelectedIfRequired = !isKit || eligibleAthletes.length === 0 || selectedAthleteId !== '';
  const isTeamSelectedIfRequired = !isKit || eligibleAthletes.length === 0 || selectedAthleteId === '' || athleteTeams.length === 0 || selectedTeamId !== '';

  const canBuy = (!isKit ? allGroupsSelected : true) && isAthleteSelectedIfRequired && isTeamSelectedIfRequired && isKitPiecesValid;

  const formatPrice = (val) => `${Number(val).toFixed(2)} €`;

  const displayPrice = isMember && product?.memberPrice
    ? product.memberPrice
    : product?.basePrice;

  const hasMemberDiscount = product?.memberPrice && product.memberPrice < product.basePrice;

  const getFinalKitSelections = () => {
    if (!isKit) return null;
    return Object.values(kitPieceSelections).map(sel => {
      const ki = product.kitItems.find(k => k.kitPieceId === sel.kitPieceId);
      if (ki?.customizationType === 'NameAndNumber') {
        return { ...sel, customName, customNumber };
      } else if (ki?.customizationType === 'NumberOnly') {
        return { ...sel, customNumber };
      }
      return sel;
    });
  };

  const handleAddToCart = () => {
    if (!selectedVariant || !product || !canBuy || addingToCart) return;
    setAddingToCart(true);
    let finalDescription = combinedDescription;
    if (isKit && selectedAthleteId) {
      const athlete = linkedAthletes.find(a => a.id.toString() === selectedAthleteId.toString());
      const athleteName = athlete ? `${athlete.firstName} ${athlete.lastName}`.trim() : '';
      let appendStr = `Atleta: ${athleteName}`;
      if (selectedTeamName) {
        appendStr += ` · Equipa: ${selectedTeamName}`;
      }
      finalDescription = finalDescription ? `${finalDescription} · ${appendStr}` : appendStr;
    }
    addItem(
      product,
      selectedVariant,
      customName,
      customNumber,
      1,
      finalDescription,
      isKit ? actualAthleteProfileId : null,
      isKit ? selectedTeamId : null,
      getFinalKitSelections()
    );
    setAddedFeedback(true);
    setTimeout(() => {
      setAddingToCart(false);
      setAddedFeedback(false);
    }, 2000);
  };

  const handleBuyNow = () => {
    if (!selectedVariant || !product || !canBuy) return;
    let finalDescription = combinedDescription;
    if (isKit && selectedAthleteId) {
      const athlete = linkedAthletes.find(a => a.id.toString() === selectedAthleteId.toString());
      const athleteName = athlete ? `${athlete.firstName} ${athlete.lastName}`.trim() : '';
      let appendStr = `Atleta: ${athleteName}`;
      if (selectedTeamName) {
        appendStr += ` · Equipa: ${selectedTeamName}`;
      }
      finalDescription = finalDescription ? `${finalDescription} · ${appendStr}` : appendStr;
    }
    addItem(
      product,
      selectedVariant,
      customName,
      customNumber,
      1,
      finalDescription,
      isKit ? actualAthleteProfileId : null,
      isKit ? selectedTeamId : null,
      getFinalKitSelections()
    );
    navigate('/loja/checkout');
  };

  const allImages = product
    ? [
      { id: 'main', imageUrl: product.imageUrl },
      ...((product.images || []).filter(i => i.imageUrl !== product.imageUrl))
    ].filter(i => i.imageUrl)
    : [];

  if (loading) return (
    <div className="produto-loading">
      <div className="loja-spinner" />
      <p>A carregar produto...</p>
    </div>
  );

  if (error || !product) return (
    <div className="produto-error">
      <i className="fas fa-exclamation-circle" />
      <p>{error || 'Produto não encontrado'}</p>
      <a href="/loja" className="btn btn-primary-blue">← Voltar à Loja</a>
    </div>
  );

  return (
    <div className="produto-page">
      <CartDrawer />

      {/* Breadcrumb */}
      <nav className="produto-breadcrumb container" aria-label="Navegação">
        <a href="/loja">Loja</a>
        <i className="fas fa-chevron-right" />
        <a href={`/loja?categoria=${product.categoryId}`}>{product.categoryName}</a>
        <i className="fas fa-chevron-right" />
        <span>{product.name}</span>
      </nav>

      <div className="container">
        <div className="produto-layout">

          {/* ── Gallery ──────────────────────────────────────────────────── */}
          <div className="produto-gallery">
            <div className="produto-main-image">
              {allImages.length > 0
                ? <img
                  src={getImageUrl(allImages[selectedImageIndex]?.imageUrl)}
                  alt={product.name}
                  className="produto-main-img"
                />
                : <div className="produto-img-placeholder">
                  <i className="fas fa-tshirt" />
                </div>
              }
              {product.isFeatured && (
                <span className="produto-featured-badge">Destaque</span>
              )}
            </div>
            {allImages.length > 1 && (
              <div className="produto-thumbnails">
                {allImages.map((img, idx) => (
                  <button
                    key={img.id}
                    className={`produto-thumb ${selectedImageIndex === idx ? 'active' : ''}`}
                    onClick={() => setSelectedImageIndex(idx)}
                    aria-label={`Imagem ${idx + 1}`}
                  >
                    <img src={getImageUrl(img.imageUrl)} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Product Info ──────────────────────────────────────────────── */}
          <div className="produto-info">
            <div className="produto-meta">
              <span className="produto-category">{product.sportName || product.categoryName}</span>
            </div>

            <h1 className="produto-name">{product.name}</h1>

            {/* Pricing */}
            <div className="produto-pricing">
              <span className="produto-price">{formatPrice(displayPrice)}</span>
              {isMember && hasMemberDiscount && (
                <>
                  <span className="produto-original">{formatPrice(product.basePrice)}</span>
                  <span className="produto-discount-badge">Preço Sócio</span>
                </>
              )}
              {!isMember && hasMemberDiscount && (
                <a href="/login" className="produto-member-hint">
                  <i className="fas fa-info-circle" />
                  Sócios têm preço especial: {formatPrice(product.memberPrice)}
                </a>
              )}
              {displayPrice > 10 && (
                <div style={{ width: '100%', marginTop: '4px', fontSize: '13px', color: '#D4AF37', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '500' }}>
                  <i className="fas fa-money-check-alt" />
                  <span>Disponível pagamento em prestações de 3 meses</span>
                </div>
              )}
            </div>

            {/* ── Dynamic variant selectors (NON-KITS) ─────────────────────────────── */}
            {!isKit && variantGroups.length > 0 && (
              <div className="produto-variants-section">
                {variantGroups.map(group => (
                  <div key={group.type} className="produto-selector-group">
                    <label className="produto-selector-label">
                      {group.type}
                      {selectedChoices[group.type] && (
                        <span className="produto-selected-value">— {selectedChoices[group.type]}</span>
                      )}
                    </label>
                    <div className="produto-sizes">
                      {group.values.map(value => {
                        const isSelected = selectedChoices[group.type] === value;
                        return (
                          <button
                            key={value}
                            className={`produto-size-btn ${isSelected ? 'active' : ''}`}
                            onClick={() => setSelectedChoices(prev => ({ ...prev, [group.type]: value }))}
                            aria-pressed={isSelected}
                            aria-label={`${group.type}: ${value}`}
                          >
                            {value}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Obrigatoriedade */}
                {!allGroupsSelected && (
                  <p className="produto-variant-hint">
                    <i className="fas fa-info-circle" style={{ marginRight: '6px', color: '#f59e0b' }} />
                    Seleciona uma opção em cada categoria para continuar.
                  </p>
                )}
              </div>
            )}

            {/* ── Kit Pieces Selectors ─────────────────────────────── */}
            {/* Customization fields */}
            {isKit && eligibleAthletes.length > 0 && (
              <div className="produto-customization-group" style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px', color: '#1e293b' }}>
                  <i className="fas fa-user" style={{ marginRight: '8px', color: 'var(--color-primary)' }}></i>
                  Atleta (Para quem é o Kit?)
                </h3>
                <div>
                  <select
                    value={selectedAthleteId}
                    onChange={(e) => {
                      setSelectedAthleteId(e.target.value);
                      setSelectedTeamId('');
                      setSelectedTeamName('');
                    }}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: '#fff' }}
                  >
                    <option value="">Selecione o atleta...</option>
                    {eligibleAthletes.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.firstName} {a.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {isKit && eligibleAthletes.length > 0 && selectedAthleteId && athleteTeams.length > 0 && (
              <div className="produto-customization-group" style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px', color: '#1e293b' }}>
                  <i className="fas fa-users" style={{ marginRight: '8px', color: 'var(--color-primary)' }}></i>
                  Equipa (Para qual equipa é o Kit?)
                </h3>
                <div>
                  <select
                    value={selectedTeamId}
                    onChange={(e) => {
                      setSelectedTeamId(e.target.value);
                      const team = athleteTeams.find(t => t.id.toString() === e.target.value);
                      setSelectedTeamName(team ? team.name : '');
                      
                      if (team && team.jerseyNumber != null) {
                        setCustomNumber(team.jerseyNumber.toString());
                      } else {
                        setCustomNumber('');
                      }
                    }}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: '#fff' }}
                  >
                    <option value="">Selecione a equipa...</option>
                    {athleteTeams.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name} {t.sportName ? `(${t.sportName})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {isKit && (kitRequiresName || kitRequiresNumber) && isAthleteSelectedIfRequired && isTeamSelectedIfRequired && (
              <div className="produto-customization-group" style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px', color: '#1e293b' }}>
                  <i className="fas fa-magic" style={{ marginRight: '8px', color: 'var(--color-primary)' }}></i>
                  Personalização do Kit
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: kitRequiresName && kitRequiresNumber ? '1fr 1fr' : '1fr', gap: '12px' }}>
                  {kitRequiresName && (
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Nome (Estampagem) *</label>
                      <input
                        type="text"
                        value={customName}
                        onChange={e => setCustomName(e.target.value)}
                        placeholder="Ex: SILVA"
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                        maxLength={15}
                      />
                    </div>
                  )}
                  {kitRequiresNumber && (
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
                        Número ({minNumber} a {maxNumber}) *
                      </label>
                      <select
                        value={customNumber}
                        onChange={e => setCustomNumber(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: '#fff' }}
                      >
                        <option value="">Selecione...</option>
                        {Array.from({ length: maxNumber - minNumber + 1 }, (_, idx) => minNumber + idx).map(num => {
                          const isTaken = takenJerseyNumbers.includes(num) && num !== selectedTeamObj?.jerseyNumber;
                          return (
                            <option key={num} value={num} disabled={isTaken}>
                              {num} {isTaken ? '(Indisponível)' : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            )}

            {isKit && product.kitItems && product.kitItems.length > 0 && (
              <div className="produto-kit-pieces-section" style={{ marginTop: '24px', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1e293b' }}>
                  <i className="fas fa-layer-group" style={{ marginRight: '8px', color: 'var(--color-primary)' }}></i>
                  Composição do Kit
                </h3>

                {product.kitItems.map(ki => {
                  return (
                    <div key={ki.kitPieceId} style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', fontWeight: '600', fontSize: '15px' }}>
                        {ki.pieceName} {ki.quantity > 1 ? `(${ki.quantity} unidades)` : ''}
                      </div>

                      {/* Size (shared across all units of this piece) */}
                      <div style={{ marginBottom: ki.customizationType !== 'None' ? '16px' : '0' }}>
                        <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>
                          Tamanho *
                        </label>
                        <div className="produto-sizes">
                          {ki.availableSizes.map(size => {
                            const isSelected = kitPieceSelections[`${ki.kitPieceId}_0`]?.selectedSize === size;
                            return (
                              <button
                                key={size}
                                className={`produto-size-btn ${isSelected ? 'active' : ''}`}
                                onClick={() => {
                                  setKitPieceSelections(prev => {
                                    const next = { ...prev };
                                    for (let i = 0; i < ki.quantity; i++) {
                                      const key = `${ki.kitPieceId}_${i}`;
                                      if (next[key]) next[key] = { ...next[key], selectedSize: size };
                                    }
                                    return next;
                                  });
                                }}
                              >
                                {size}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {product.isCustomizable && !isKit && isAthleteSelectedIfRequired && isTeamSelectedIfRequired && (
              <div className="produto-customization-group" style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px', color: '#1e293b' }}>
                  <i className="fas fa-magic" style={{ marginRight: '8px', color: 'var(--color-primary)' }}></i>
                  Personalização (Opcional)
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Nome a estampar</label>
                    <input
                      type="text"
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                      placeholder="Ex: SILVA"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                      maxLength={15}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
                      Número
                    </label>
                    <input
                      type="number"
                      value={customNumber}
                      onChange={(e) => setCustomNumber(e.target.value)}
                      placeholder="Ex: 10"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                      min={0}
                      max={99}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* CTAs */}
            <div className="produto-ctas">
              <button
                id="add-to-cart-btn"
                className={`produto-btn-cart ${addedFeedback ? 'success' : ''}`}
                onClick={handleAddToCart}
                disabled={!canBuy || addingToCart}
              >
                {addedFeedback
                  ? <><i className="fas fa-check" /> Adicionado!</>
                  : !isKit && !allGroupsSelected
                    ? <><i className="fas fa-hand-pointer" /> Seleciona as opções</>
                    : (!isAthleteSelectedIfRequired)
                      ? <><i className="fas fa-user" /> Seleciona o atleta</>
                      : (!isTeamSelectedIfRequired)
                        ? <><i className="fas fa-users" /> Seleciona a equipa</>
                        : (isKit && !isKitPiecesValid)
                          ? <><i className="fas fa-layer-group" /> Preenche todas as opções do kit</>
                          : <><i className="fas fa-shopping-bag" /> Adicionar ao Carrinho</>
                }
              </button>
              <button
                id="buy-now-btn"
                className="produto-btn-buy"
                onClick={handleBuyNow}
                disabled={!canBuy}
              >
                Comprar Agora
              </button>
            </div>

            {/* Description */}
            {product.description && (
              <div className="produto-description">
                <h2>Descrição</h2>
                <p>{product.description}</p>
              </div>
            )}

            {/* Trust badges */}
            <div className="produto-trust">
              <div className="produto-trust-item">
                <i className="fas fa-shield-alt" />
                <span>Produto Oficial</span>
              </div>
              <div className="produto-trust-item">
                <i className="fas fa-lock" />
                <span>Pagamento Seguro</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating cart */}
      {totalItems > 0 && (
        <button
          className="loja-cart-fab"
          onClick={() => setIsDrawerOpen(true)}
          aria-label={`Carrinho: ${totalItems} itens`}
        >
          <i className="fas fa-shopping-bag" />
          <span>Carrinho</span>
          <span className="loja-cart-fab-count">{totalItems}</span>
        </button>
      )}
    </div>
  );
}
